import type { FC, PropsWithChildren, ReactElement } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { remoteLogger } from "@/utils/remoteLogger";
import { fetchIdToken } from "./fetchIdToken";
import { Button } from "@/components/button/Button";

const PROVIDER_NAME = "Google";

interface GoogleLoginProps {
  children: ReactElement | Array<ReactElement> | string;
  className?: string;
  onSuccess: (tokenId: string) => void;
  onError?: (provider: string, error: unknown) => void;
  onClick?: () => void;
}

/**
 * For the Google authentication we used the method described on the following link.
 * https://github.com/MomenSherif/react-oauth/issues/12#issuecomment-1131408898
 *
 * Google provides "id_token" only on their custom button, but it doest not work for us,
 * we need to implement a button that matches our style. For these reasons we have to use the auth-code flow
 * to log in, and implement an endpoint that fetches the id_token for us.
 *
 * The steps that it follows are:
 *  1 - the user to log in using its google account.
 *  2 - after the user has loged in google returns a "code".
 *  3 - the code is sent to the "auth/google" endpoint that will do a request to google and will return a token_id.
 *  4 - we use the token_id to log in the user to our system.
 */
const GoogleLogin: FC<PropsWithChildren<GoogleLoginProps>> = (
  props,
): ReactElement | null => {
  const { onSuccess, onError, className, children, onClick } = props;

  const login = useGoogleLogin({
    flow: "auth-code",

    onError: () => onError?.(PROVIDER_NAME, ""),
    onSuccess: ({ code }) => {
      //The onSuccess function is defined as () => void, so it is not possible to use async await
      void fetchIdToken(code)
        .then((tokenId) => {
          onSuccess(tokenId);
        })
        .catch((err) => {
          remoteLogger.error(err);
          onError?.(PROVIDER_NAME, "while fetching id token");
        });
    },
  });

  return (
    <Button
      className={className}
      onClick={() => {
        login();
        onClick?.();
      }}
    >
      {children}
    </Button>
  );
};

export { GoogleLogin };
